import React, { useState } from "react";
import { Link } from "react-router-dom";

const APP_STORE_URL = "https://apps.apple.com/";

type Screenshot = {
  src: string;
  alt: string;
};

const portraitScreenshots: Screenshot[] = [
  { src: "/momental/assets/screenshot-main-menu.png", alt: "Momental main menu" },
  { src: "/momental/assets/screenshot-one-way-walls.png", alt: "One-way walls puzzle" },
  { src: "/momental/assets/screenshot-tunnels.png", alt: "Tunnels mechanic" },
  { src: "/momental/assets/screenshot-rotating-tiles.png", alt: "Rotating tiles puzzle" },
  { src: "/momental/assets/screenshot-rotating-tunnels.png", alt: "Rotating tunnels" },
  { src: "/momental/assets/screenshot-portals.png", alt: "Portals puzzle" },
];

const landscapeScreenshot: Screenshot = {
  src: "/momental/assets/screenshot-level-editor.png",
  alt: "In-game level editor",
};

const keyFeatures = [
  "Momentum-based sliding puzzles on a hexagonal grid",
  "Rotating tiles, tunnels, portals, and other mechanics that redirect the ball's path",
  "200+ handcrafted levels across 9 puzzle worlds",
  "Replay challenges that add new objectives to every level",
  "In-game level editor and community hub for creating and sharing puzzles",
  "Original soundtrack with adaptive music that evolves as puzzles progress",
];

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #fbfbfc 0%, #f6f7fb 60%, #fafafa 100%)",
    color: "#222",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  } as React.CSSProperties,

  container: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "48px 24px 64px",
    lineHeight: 1.7,
  } as React.CSSProperties,

  heroSection: {
    textAlign: "center" as const,
    marginBottom: "48px",
  } as React.CSSProperties,

  heroTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    marginBottom: "16px",
  } as React.CSSProperties,

  appIcon: {
    width: "80px",
    height: "80px",
    borderRadius: "18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    flexShrink: 0,
  } as React.CSSProperties,

  title: {
    fontSize: "3rem",
    fontWeight: 800,
    color: "#111",
    margin: 0,
  } as React.CSSProperties,

  subtitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#2d3748",
    margin: "12px 0 16px",
    lineHeight: 1.5,
    textAlign: "left" as const,
  } as React.CSSProperties,

  tagline: {
    fontSize: "1.1rem",
    color: "#4a5568",
    margin: "0 0 24px",
    lineHeight: 1.6,
    textAlign: "left" as const,
  } as React.CSSProperties,

  buttonGroup: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
    marginBottom: "16px",
  } as React.CSSProperties,

  appStoreBadge: {
    cursor: "pointer",
    height: "48px",
    width: "auto",
  } as React.CSSProperties,

  modalOverlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "24px",
  } as React.CSSProperties,

  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "40px 32px",
    maxWidth: "440px",
    width: "100%",
    textAlign: "center" as const,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    position: "relative" as const,
  } as React.CSSProperties,

  modalClose: {
    position: "absolute" as const,
    top: "12px",
    right: "16px",
    background: "none",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    color: "#888",
    lineHeight: 1,
  } as React.CSSProperties,

  modalTitle: {
    fontSize: "1.6rem",
    fontWeight: 700,
    color: "#111",
    margin: "0 0 8px 0",
  } as React.CSSProperties,

  modalSubtitle: {
    fontSize: "1.1rem",
    color: "#4a5568",
    margin: "0 0 24px 0",
    lineHeight: 1.5,
  } as React.CSSProperties,

  modalForm: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
    flexWrap: "wrap" as const,
  } as React.CSSProperties,

  modalInput: {
    flex: 1,
    minWidth: "200px",
    padding: "12px 16px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "1px solid #cbd5e0",
    outline: "none",
  } as React.CSSProperties,

  modalSubmitButton: {
    backgroundColor: "#2b6cb0",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #245c98",
    fontWeight: 700,
    fontSize: "1rem",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  } as React.CSSProperties,

  modalSuccess: {
    color: "#38a169",
    fontSize: "1.05rem",
    fontWeight: 600,
    margin: "16px 0 0 0",
  } as React.CSSProperties,

  secondaryButton: {
    display: "inline-block",
    backgroundColor: "#fff",
    color: "#2b6cb0",
    padding: "12px 24px",
    borderRadius: "10px",
    textDecoration: "none",
    fontWeight: 700,
    fontSize: "1rem",
    border: "1px solid #90cdf4",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,


  section: {
    marginBottom: "48px",
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: "#2c5282",
    margin: "0 0 20px 0",
    paddingBottom: "10px",
    borderBottom: "2px solid #cbd5e0",
  } as React.CSSProperties,

  videoContainer: {
    width: "100%",
    maxWidth: "600px",
    margin: "0 auto 24px",
    aspectRatio: "9 / 19.5",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#000",
  } as React.CSSProperties,

  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  } as React.CSSProperties,

  videoLabel: {
    textAlign: "center" as const,
    color: "#666",
    fontSize: "0.95rem",
    marginBottom: "8px",
  } as React.CSSProperties,

  featureList: {
    margin: 0,
    paddingLeft: "24px",
    color: "#2d3748",
    fontSize: "1.05rem",
  } as React.CSSProperties,

  featureItem: {
    marginBottom: "10px",
  } as React.CSSProperties,

  screenshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "16px",
  } as React.CSSProperties,

  screenshotCard: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  } as React.CSSProperties,

  landscapeScreenshotCard: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
  } as React.CSSProperties,

  screenshotImg: {
    width: "100%",
    height: "auto",
    display: "block",
  } as React.CSSProperties,

  footer: {
    marginTop: "64px",
    paddingTop: "24px",
    borderTop: "2px solid #e0e0e0",
    textAlign: "center" as const,
    color: "#666",
    fontSize: "0.9rem",
  } as React.CSSProperties,

  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "12px",
  } as React.CSSProperties,

  footerLink: {
    color: "#2b6cb0",
    textDecoration: "none",
    fontWeight: 600,
    fontSize: "0.95rem",
  } as React.CSSProperties,

  emailSignupBox: {
    backgroundColor: "#ebf8ff",
    border: "2px solid #90cdf4",
    borderRadius: "12px",
    padding: "32px",
    textAlign: "center" as const,
    marginBottom: "48px",
  } as React.CSSProperties,

  emailSignupTitle: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#2c5282",
    margin: "0 0 12px 0",
  } as React.CSSProperties,

  emailSignupText: {
    fontSize: "1.05rem",
    color: "#4a5568",
    margin: "0 0 20px 0",
  } as React.CSSProperties,

  emailForm: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap" as const,
    maxWidth: "500px",
    margin: "0 auto",
  } as React.CSSProperties,

  emailInput: {
    flex: 1,
    minWidth: "250px",
    padding: "12px 16px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "1px solid #cbd5e0",
    outline: "none",
  } as React.CSSProperties,

  emailSubmitButton: {
    backgroundColor: "#2b6cb0",
    color: "#fff",
    padding: "12px 24px",
    borderRadius: "8px",
    border: "1px solid #245c98",
    fontWeight: 700,
    fontSize: "1rem",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  } as React.CSSProperties,
};

const MomentalMarketing: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Hero Section */}
        <section style={styles.heroSection}>
          <div style={styles.heroTop}>
            <img
              src="/momental-icon.png"
              alt="Momental app icon"
              style={styles.appIcon}
            />
            <h1 style={styles.title}>Momental</h1>
          </div>
          <p style={styles.subtitle}>
            A momentum-driven puzzle game of sliding ball mazes and chain-reaction mechanics.
          </p>
          <p style={styles.tagline}>
            Momental is a momentum-based puzzle game where a rolling ball slides through mazes on a hexagonal grid, bouncing off walls and triggering portals, tunnels, and rotating mechanisms that redirect its path.
          </p>
          <div style={styles.buttonGroup}>
            <a
              href="https://apps.apple.com/app/id6745159953"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
                alt="Download on the App Store"
                style={styles.appStoreBadge}
              />
            </a>
          </div>
        </section>

        {/* Gameplay Video */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Gameplay Video</h2>
          <div style={styles.videoContainer}>
            <video
              style={styles.video}
              controls
              poster="/momental/assets/video-poster.png"
            >
              <source src="/momental/assets/gameplay-30s.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <p style={styles.videoLabel}>30-second gameplay preview</p>
        </section>

        {/* Key Features */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Key Features</h2>
          <ul style={styles.featureList}>
            {keyFeatures.map((feature, idx) => (
              <li key={idx} style={styles.featureItem}>
                {feature}
              </li>
            ))}
          </ul>
        </section>

        {/* Screenshots */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Screenshots</h2>
          <div style={styles.screenshotGrid}>
            {portraitScreenshots.map((s, idx) => (
              <div key={idx} style={styles.screenshotCard}>
                <img src={s.src} alt={s.alt} style={styles.screenshotImg} />
              </div>
            ))}
          </div>
          <div style={styles.landscapeScreenshotCard}>
            <img src={landscapeScreenshot.src} alt={landscapeScreenshot.alt} style={styles.screenshotImg} />
          </div>
        </section>

        {/* Email Signup */}
        <section style={styles.emailSignupBox}>
          <h2 style={styles.emailSignupTitle}>Stay Updated</h2>
          <p style={styles.emailSignupText}>
            Get notified when Momental launches on the App Store
          </p>
          <form
            style={styles.emailForm}
            onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const email = formData.get('email');
              try {
                const response = await fetch('/api/email/submit', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email, source: 'momental-main' })
                });
                if (response.ok) {
                  (e.target as HTMLFormElement).reset();
                  alert('Thanks! We\'ll notify you when Momental launches.');
                } else {
                  alert('Something went wrong. Please try again.');
                }
              } catch (error) {
                alert('Something went wrong. Please try again.');
              }
            }}
          >
            <input
              type="email"
              name="email"
              placeholder="Enter your email"
              required
              style={styles.emailInput}
            />
            <button type="submit" style={styles.emailSubmitButton}>
              Notify Me
            </button>
          </form>
        </section>

        {/* Coming Soon Modal */}
        {showModal && (
          <div
            style={styles.modalOverlay}
            onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <div style={styles.modalContent}>
              <button style={styles.modalClose} onClick={() => setShowModal(false)} aria-label="Close">
                &times;
              </button>
              <img
                src="/momental-icon.png"
                alt="Momental"
                style={{ width: 64, height: 64, borderRadius: 14, marginBottom: 16 }}
              />
              <h2 style={styles.modalTitle}>Coming Spring 2026</h2>
              <p style={styles.modalSubtitle}>
                Momental is set for release on the App Store in Spring 2026. Enter your email to be notified when it launches.
              </p>
              {!emailSubmitted ? (
                <form
                  style={styles.modalForm}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const email = formData.get('email');
                    try {
                      const response = await fetch('/api/email/submit', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, source: 'momental-modal' })
                      });
                      if (response.ok) {
                        setEmailSubmitted(true);
                      } else {
                        alert('Something went wrong. Please try again.');
                      }
                    } catch (error) {
                      alert('Something went wrong. Please try again.');
                    }
                  }}
                >
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    required
                    style={styles.modalInput}
                  />
                  <button type="submit" style={styles.modalSubmitButton}>
                    Notify Me
                  </button>
                </form>
              ) : (
                <p style={styles.modalSuccess}>Thanks! We'll let you know when Momental launches.</p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer style={styles.footer}>
          <div style={styles.footerLinks}>
            <a
              href={APP_STORE_URL}
              style={styles.footerLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              App Store
            </a>
            <a href="https://momental.craftword.game/Support" style={styles.footerLink}>
              Support
            </a>
            <a href="https://momental.craftword.game/Privacy" style={styles.footerLink}>
              Privacy
            </a>
          </div>
          <div>© 2026 Momental</div>
        </footer>
      </div>
    </div>
  );
};

export default MomentalMarketing;
